import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as Toast from '@givebutter/primitives/toast';
import { unmaskNumber } from '@givebutter/util';
import { faTrophy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { transformAddressToRequest } from 'api/account';
import {
    fetchAuctionBidder,
    fetchBidderPaymentPreview,
    processBidderPayment,
    updateAuctionBidder,
} from 'api/campaigns';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import * as ItemWinnerForm from 'components/pages/campaign-settings/campaign-auction/item-details/declare-winner/ItemWinnerForm';
import { STEPS } from 'components/pages/campaign-settings/campaign-auction/item-details/declare-winner/constants';
import { DeclareWinnerDialogForm } from 'components/pages/campaign-settings/campaign-auction/item-details/declare-winner/types';
import { useItemWinnerStep } from 'components/pages/campaign-settings/campaign-auction/item-details/declare-winner/useItemWinnerStep';
import {
    getItemSubtotal,
    getRequiresShippingAddress,
} from 'components/pages/campaign-settings/campaign-auction/item-details/declare-winner/utils';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaignItem } from 'customHooks/useCurrentCampaignItem';
import { maskedDateToServerDateTime } from 'helpers/dates';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import {
    AuctionItem,
    Bidder,
    BidderInvoiceType,
    BidderPaymentPreview,
    Transaction,
} from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const DeclareWinnerDrawer = () => {
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const queryClient = useQueryClient();
    const { currentStep, setCurrentStep, canProgress, nextStep, prevStep, isFirstStep } =
        useItemWinnerStep();

    const { data: item } = useCurrentCampaignItem();

    const [paymentPreview, setPaymentPreview] = useState<BidderPaymentPreview | null>(null);
    const { success, error } = Toast.useToast();
    const { goBack } = useDialogRoute();

    const auctionId = item.auction_id;
    const campaignId = item.campaign_id;
    const itemId = item.id;
    const highestBidderId = item.highest_bid?.bidder.id;
    const shouldFetch = Boolean(highestBidderId);

    const { data: bidder } = useSuspenseQuery({
        queryKey: campaignQueryKeys.auctionBidderById(
            campaignId,
            auctionId,
            Number(highestBidderId),
        ),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchAuctionBidder(campaignId, auctionId, Number(highestBidderId)),
                shouldFetch,
            ),
    });

    const methods = Form.useGivebutterForm<DeclareWinnerDialogForm>({
        defaultValues: defaultForm(item, bidder as Bidder),
    });

    const { watch, handleSubmit, resetFormButton, setValue, setError } = methods;
    const selectedBidder = watch('selectedBidder');
    const rawSelectedBidder = selectedBidder?.[0];
    const currentBidder = rawSelectedBidder?.value || bidder;

    const { mutate: requestBidderPaymentPreview, isPending: isRequestingPaymentInfo } = useMutation(
        {
            mutationFn: ({ highestBid, buyNowPrice, paymentOption }: DeclareWinnerDialogForm) => {
                const subtotal = getItemSubtotal(item, highestBid, buyNowPrice);
                return fetchBidderPaymentPreview(campaignId, itemId, {
                    amount: unmaskNumber(subtotal ?? 0),
                    action: paymentOption,
                });
            },
            onSuccess: (preview: BidderPaymentPreview) => {
                resetFormButton();
                setPaymentPreview(preview);
                setCurrentStep(STEPS.FINALIZE_WINNER);
            },
            onError: (e: GivebutterAxiosError) => {
                error(e.response?.data.message ?? 'Unknown error occurred');
            },
        },
    );

    const { mutate: handleBidderPayment, isPending: isProcessingPayment } = useMutation({
        mutationFn: ({
            highestBid,
            buyNowPrice,
            paymentOption,
            paymentMethodId,
            offlinePaymentMethod,
            checkNumber,
            checkDepositDate,
        }: DeclareWinnerDialogForm) => {
            const subtotal = getItemSubtotal(item, highestBid, buyNowPrice);
            const bidderAddress = currentBidder?.address;

            return processBidderPayment(campaignId, itemId, {
                amount: unmaskNumber(subtotal),
                action: paymentOption,
                ...(currentBidder && { bidder_id: currentBidder.id }),
                ...(paymentOption === 'charge' &&
                    paymentMethodId && {
                        payment_method_id: paymentMethodId,
                    }),
                ...(paymentOption === 'charge' &&
                    bidderAddress && {
                        address_id: bidderAddress.id,
                    }),
                ...(paymentOption === 'offline' &&
                    offlinePaymentMethod && {
                        offline_payment_data: getOfflinePaymentData(
                            offlinePaymentMethod,
                            checkNumber,
                            checkDepositDate,
                        ),
                    }),
            });
        },
        onSuccess: (transaction: Transaction, values: DeclareWinnerDialogForm) => {
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.items(campaignId),
                type: 'all',
            });

            const { paymentOption } = values;

            const isTransactionAction = paymentOption === 'charge' || paymentOption === 'offline';
            const bodyText = isTransactionAction
                ? 'An email receipt for the transaction has been sent to'
                : 'A payment request has been sent to';

            success(
                <div className={'flex flex-col items-start gap-1.5 text-sm'}>
                    <p className="font-medium">Winner successfully declared!</p>
                    <div className="flex flex-col gap-2">
                        <p>
                            <Toast.Close
                                className="font-medium text-blue-500 hover:underline"
                                onClick={() =>
                                    history.push(
                                        `/accounts/${accountId}/campaigns/${item.campaign_id}/auction/items/${item.id}`,
                                    )
                                }
                            >
                                {item.name}
                            </Toast.Close>{' '}
                            is now sold. {bodyText} {currentBidder?.first_name}.
                        </p>
                        <div className="flex gap-4">
                            <Toast.Close className="block text-sm font-medium hover:underline">
                                Dismiss
                            </Toast.Close>
                            {isTransactionAction && (
                                <Toast.Close
                                    className="text-sm font-medium text-blue-500 hover:underline"
                                    onClick={() =>
                                        history.push(
                                            `/accounts/${accountId}/transactions/${transaction.id}`,
                                        )
                                    }
                                >
                                    View transaction
                                </Toast.Close>
                            )}
                        </div>
                    </div>
                </div>,
                { duration: Infinity, size: 'md' },
            );
            goBack();
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            error(data?.message ?? 'Unknown error occurred');

            if (typeof errors === 'object') {
                for (let field in errors) {
                    const mappedField = transformServerKeyIntoFormKey(field);
                    if (mappedField) {
                        setError(mappedField, {
                            message: errors[field][0],
                        });
                    }
                }
            }
            resetFormButton();
        },
    });

    const { mutate: updateBidderShippingAddress, isPending: isUpdatingBidderShippingAddress } =
        useMutation({
            mutationFn: (formData: DeclareWinnerDialogForm) => {
                const state = formData.country === 'USA' ? formData.stateUs : formData.stateIntl;

                return updateAuctionBidder(campaignId, auctionId, currentBidder!.id, {
                    shipping_address: transformAddressToRequest({ ...formData, state }),
                });
            },
            onSuccess: (updatedBidder: Bidder) => {
                queryClient.invalidateQueries({
                    queryKey: campaignQueryKeys.auctionBidders(campaignId, auctionId),
                });

                setValue('selectedBidder', [
                    {
                        id: String(updatedBidder.id),
                        label: `${updatedBidder.first_name} ${updatedBidder.last_name}`,
                        value: updatedBidder,
                    },
                ]);

                setCurrentStep(STEPS.CHOOSE_PAYMENT);
            },
            onError: (e: GivebutterAxiosError) => {
                error(e.response?.data?.message ?? `Unable to update bidder address.`);
                resetFormButton();
            },
        });

    const submit = (values: DeclareWinnerDialogForm) => {
        switch (currentStep) {
            case STEPS.DECLARE_WINNER:
            case STEPS.ENTER_WINNER_INFO:
                if (!currentBidder?.address) {
                    updateBidderShippingAddress(values);
                } else {
                    nextStep();
                }
                break;
            case STEPS.CHOOSE_PAYMENT:
                requestBidderPaymentPreview(values);
                break;
            case STEPS.FINALIZE_WINNER:
                handleBidderPayment(values);
                break;
            default:
                nextStep();
        }
    };

    const isSubmitting =
        isUpdatingBidderShippingAddress || isRequestingPaymentInfo || isProcessingPayment;

    return (
        <Form.Provider {...methods}>
            <ItemWinnerForm.Root
                item={item}
                bidder={currentBidder as Bidder}
                currentStep={currentStep}
                paymentPreview={paymentPreview}
            >
                <form className={'contents'} onSubmit={handleSubmit(values => submit(values))}>
                    <Drawer.Header>
                        <div className="flex items-center gap-4">
                            <div className="rounded-lg border p-3 text-gray-400">
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={faTrophy}
                                    className="text-purple-600"
                                />
                            </div>
                            <Drawer.Title>Declare winner</Drawer.Title>
                        </div>
                        <Drawer.Close />
                    </Drawer.Header>
                    <Drawer.Body className="flex flex-col gap-6">
                        <div className="flex flex-col gap-4">
                            <div>
                                <p className="font-medium">{item.name}</p>
                                <ItemWinnerForm.StepDescription />
                            </div>
                            <ItemWinnerForm.DeclareWinnerStep />
                            <ItemWinnerForm.EnterInfoStep />
                            <ItemWinnerForm.ChoosePaymentStep />
                            <ItemWinnerForm.FinalizeWinnerStep />
                        </div>
                    </Drawer.Body>
                    <Drawer.Footer>
                        <div
                            className={
                                'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                            }
                        >
                            {isFirstStep ? (
                                <Drawer.Cancel asChild>
                                    <Button
                                        variant="transparent"
                                        onClick={() => goBack()}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                </Drawer.Cancel>
                            ) : (
                                <Button
                                    variant="transparent"
                                    onClick={prevStep}
                                    disabled={isSubmitting}
                                >
                                    Back
                                </Button>
                            )}
                            <div className={'flex flex-wrap-reverse items-center gap-4'}>
                                <ItemWinnerForm.NextButton
                                    isSubmitting={isSubmitting}
                                    canProgress={canProgress}
                                    goToNextStep={nextStep}
                                />
                            </div>
                        </div>
                    </Drawer.Footer>
                </form>
            </ItemWinnerForm.Root>
        </Form.Provider>
    );
};

const defaultForm = (item: AuctionItem, bidder: Bidder) => {
    const requireAddress = getRequiresShippingAddress(bidder, item);

    return {
        paymentOption: 'charge' as BidderInvoiceType,
        buyNowPrice: item.price?.toString() ?? '',
        highestBid: item.highest_bid?.amount?.toString() ?? '',
        selectedBidder: bidder
            ? [
                  {
                      id: bidder.id.toString(),
                      label: `${bidder.first_name} ${bidder.last_name}`,
                      value: bidder,
                  },
              ]
            : null,
        paymentMethodId: bidder?.payment_methods?.[0]?.id ?? null,
        ...(requireAddress
            ? {
                  line1: '',
                  line2: '',
                  city: '',
                  stateUs: '',
                  stateIntl: '',
                  zip: '',
                  country: 'USA',
              }
            : {}),
        offlinePaymentMethod: 'cash',
    };
};

const getOfflinePaymentData = (method: string, checkNumber?: string, checkDepositDate?: string) => {
    const baseData = { method };

    if (method === 'check') {
        return {
            ...baseData,
            check_number: checkNumber,
            check_deposited_at:
                checkDepositDate && checkDepositDate.length > 0
                    ? maskedDateToServerDateTime(checkDepositDate)
                    : null,
        };
    }

    return baseData;
};

const transformServerKeyIntoFormKey = (serverKey: string): keyof DeclareWinnerDialogForm | null => {
    switch (serverKey) {
        case 'offline_payment_data.check_number':
            return 'checkNumber';
        case 'offline_payment_data.check_deposited_at':
            return 'checkDepositDate';
        default:
            return null;
    }
};

export { DeclareWinnerDrawer };
